import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';

import { SchedulingAgreementCreateModal } from './SchedulingAgreementCreateModal.jsx';
import { SchedulingAgreementDetailModal } from './SchedulingAgreementDetailModal.jsx';

export function SchedulingAgreementsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['inventory', 'scheduling-agreements'], queryFn: () => inventoryApi.listSchedulingAgreements().then((r) => r.items) });
  const rows = list.data || [];

  const columns = [
    { key: 'number', label: t('lp.col.number'), render: (r) => <span className="mono">{r.agreementNumber}</span> },
    { key: 'supplier', label: t('lp.col.supplier'), render: (r) => r.supplierName },
    { key: 'product', label: t('lp.col.product'), render: (r) => <span><strong>{r.productSku}</strong> <span className="muted text-sm">{r.productName}</span></span> },
    { key: 'planned', label: t('lp.col.planned'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.plannedTotal)}</span> },
    { key: 'received', label: t('lp.col.received'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.receivedTotal)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={r.status === 'ACTIVE' ? 'success' : 'neutral'}>{t(`lp.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('lp.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('lp.title')}</h1>
          <p className="muted">{t('lp.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('lp.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('lp.metric.active')} value={String(rows.filter((r) => r.status === 'ACTIVE').length)} trendVariant="success" />
        <MetricCard label={t('lp.metric.total')} value={String(rows.length)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('lp.empty')} />

      <SchedulingAgreementCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <SchedulingAgreementDetailModal agreementId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
